/**
 * Pre-compute monthly import report totals (brand + group wise) and store in Firestore
 * so Monthly Sale Report (import) loads instantly from one document.
 */
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  lineAmountOnly,
  lineBillAmountHeaderOnly,
  normalizeVoucherType,
  resolveImportVoucherTotal,
} from "../features/salesDataImport/services/salesLineChunksReader";
import {
  appBranchFromImportRow,
  filterImportRowsForMonthlyReport,
  includeRowInMonthlyImportReport,
  isCreditNoteVoucherType,
  isMainBranchInwardSupplyPurchaseRow,
  isMainBranchTransferOutPurchaseDeductionRow,
  isMainBranchImportSaleVoucherRow,
  isImportSaleVoucherRow,
  isBranchTransferVoucherRow,
  isTransferOutNewVoucherRow,
  isTransferInVoucherRow,
  isTransferOutDirectionVoucherRow,
  isTransferInDirectionVoucherRow,
  importRowBranchId,
  importRowBrandNameField,
  loadSalesImportRows,
} from "./salesImportTransactionDataService";
import { MONTHLY_AUTOS_BRANDS, normalizeBrand } from "./monthlyAutosReportService";

export const COL_MONTHLY_IMPORT_PRECALC = "monthly_import_precalc";

const REPORT_TITLE_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function monthWindow(year, monthIndex) {
  const start = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const end = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

export function monthlyImportPrecalcDocId(year, monthIndex, branchFilter) {
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number(monthIndex)));
  const branch = String(branchFilter || "Main");
  return `${y}-${mi}-${branch}`;
}

function emptyBucket() {
  return { sale: 0, purchase: 0, transferOut: 0, transferIn: 0, saleReturn: 0 };
}

function addBucket(target, source) {
  target.sale += source.sale || 0;
  target.purchase += source.purchase || 0;
  target.transferOut += source.transferOut || 0;
  target.transferIn += source.transferIn || 0;
  target.saleReturn += source.saleReturn || 0;
}

function rawGroupFromRow(row) {
  return String(row.itemGroup ?? row.itemgroup ?? row["Item Group"] ?? "").trim() || "Unknown";
}

function brandKeyFromRow(row) {
  const raw = importRowBrandNameField(row) || String(row.itemGroup || "").trim();
  const normalized = normalizeBrand(raw);
  return MONTHLY_AUTOS_BRANDS.includes(normalized) ? normalized : normalized === "MISCELLANEOUS" ? "MISCELLANEOUS" : raw || "Unknown";
}

function classifyRowAmount(row, branchFilter) {
  const amt = Math.abs(lineAmountOnly(row));
  if (!(amt > 0)) return null;

  const vt = normalizeVoucherType(row);
  if (appBranchFromImportRow(row) !== branchFilter) return null;

  if (
    branchFilter === "Main" &&
    (isTransferOutNewVoucherRow(row) || isTransferInVoucherRow(row)) &&
    importRowBranchId(row) !== "1"
  ) {
    return null;
  }

  if (isCreditNoteVoucherType(vt)) {
    return { kind: "saleReturn", amount: amt };
  }

  if (branchFilter === "Main") {
    if (isMainBranchImportSaleVoucherRow(row)) {
      return { kind: "sale", amount: amt };
    }
    if (isMainBranchInwardSupplyPurchaseRow(row)) {
      return { kind: "purchase", amount: amt };
    }
    if (isMainBranchTransferOutPurchaseDeductionRow(row)) {
      return { kind: "purchaseDeduct", amount: amt };
    }
  } else {
    if (isImportSaleVoucherRow(row)) {
      return { kind: "sale", amount: amt };
    }
    const vtLower = vt.toLowerCase();
    if (vtLower.includes("inward") || isMainBranchInwardSupplyPurchaseRow(row)) {
      return { kind: "purchase", amount: amt };
    }
  }

  return null;
}

/** Transfer vouchers: line Amount is often 0 — totals use BillAmount at voucher level (same as monthly report). */
function isTransferRowForBreakdown(row, branchFilter) {
  if (appBranchFromImportRow(row) !== branchFilter) return false;
  if (
    branchFilter === "Main" &&
    (isTransferOutNewVoucherRow(row) || isTransferInVoucherRow(row)) &&
    importRowBranchId(row) !== "1"
  ) {
    return false;
  }
  return isBranchTransferVoucherRow(row);
}

function dimensionKeyFromRow(row, dimension) {
  return dimension === "group" ? rawGroupFromRow(row) : brandKeyFromRow(row);
}

function allocateTransferVoucherToBuckets(voucherRows, dimension, branchFilter, ensure) {
  if (!voucherRows?.length) return;
  const head = voucherRows[0];
  const out = isTransferOutDirectionVoucherRow(head);
  const inn = isTransferInDirectionVoucherRow(head);
  if (!out && !inn) return;
  const kind = out ? "transferOut" : "transferIn";
  const winsBranchPurchaseDeduct =
    branchFilter === "Main" && out && isMainBranchTransferOutPurchaseDeductionRow(head);

  const addToBucket = (key, amount) => {
    const bucket = ensure(key);
    bucket[kind] += amount;
    if (winsBranchPurchaseDeduct) bucket.purchase -= amount;
  };

  let lineSum = 0;
  let maxBill = 0;
  const parts = [];
  for (const row of voucherRows) {
    const amt = Math.abs(lineAmountOnly(row));
    if (amt > 0) lineSum += amt;
    maxBill = Math.max(maxBill, lineBillAmountHeaderOnly(row));
    parts.push({ key: dimensionKeyFromRow(row, dimension), amt });
  }

  const voucherTotal = resolveImportVoucherTotal(lineSum, maxBill);
  if (!(voucherTotal > 0)) return;

  const withAmt = parts.filter((p) => p.amt > 0);
  if (!withAmt.length) {
    const keys = [...new Set(parts.map((p) => p.key || "Unknown"))];
    const share = voucherTotal / Math.max(1, keys.length);
    for (const key of keys) addToBucket(key, share);
    return;
  }

  const partsSum = withAmt.reduce((s, p) => s + p.amt, 0);
  const scale = partsSum > voucherTotal * 1.001 ? voucherTotal / partsSum : 1;
  for (const { key, amt } of withAmt) {
    addToBucket(key, amt * scale);
  }
}

function aggregateRowsByDimension(rows, branchFilter, dimension) {
  const map = new Map();
  const transferGroups = new Map();

  const ensure = (key) => {
    if (!map.has(key)) map.set(key, emptyBucket());
    return map.get(key);
  };

  for (const row of rows || []) {
    if (isTransferRowForBreakdown(row, branchFilter)) {
      const vn = String(row.voucherNo || "").trim();
      if (!vn) continue;
      const gk = `${normalizeVoucherType(row)}::${vn}`;
      if (!transferGroups.has(gk)) transferGroups.set(gk, []);
      transferGroups.get(gk).push(row);
      continue;
    }

    const classified = classifyRowAmount(row, branchFilter);
    if (!classified) continue;

    const key = dimensionKeyFromRow(row, dimension);
    const bucket = ensure(key);

    if (classified.kind === "sale") bucket.sale += classified.amount;
    else if (classified.kind === "purchase") bucket.purchase += classified.amount;
    else if (classified.kind === "purchaseDeduct") bucket.purchase -= classified.amount;
    else if (classified.kind === "saleReturn") bucket.saleReturn += classified.amount;
  }

  for (const voucherRows of transferGroups.values()) {
    allocateTransferVoucherToBuckets(voucherRows, dimension, branchFilter, ensure);
  }

  return [...map.entries()]
    .map(([key, amounts]) => ({ key, label: key, ...amounts }))
    .sort((a, b) => b.sale + b.purchase - (a.sale + a.purchase));
}

async function loadCurrentMonthRows(year, monthIndex, branchFilter) {
  const { start, end } = monthWindow(year, monthIndex);
  const rowFilter =
    branchFilter === "Main" || branchFilter === "Wholesale"
      ? (row) => includeRowInMonthlyImportReport(row, branchFilter)
      : null;
  const rows = await loadSalesImportRows({
    fromDate: ymdLocal(start),
    toDate: ymdLocal(end),
    rowFilter,
    rowFilterKey:
      branchFilter === "Main" ? "main-full" : branchFilter === "Wholesale" ? "wholesale-full" : "all",
  });
  if (rowFilter) return rows;
  return filterImportRowsForMonthlyReport(rows, branchFilter);
}

export function buildByBrandSaleReturn(report) {
  const sr = report?.saleReturnByBrand || report?.saleReturnByBranch || {};
  const oe = report?.saleReturnOeByBrand || {};
  const ord = report?.saleReturnOrdByBrand || {};

  return MONTHLY_AUTOS_BRANDS.map((brand) => ({
    key: brand,
    label: brand,
    saleReturn: Number(sr[brand]) || 0,
    oe: Number(oe[brand]) || 0,
    ordinary: Number(ord[brand]) || 0,
  }))
    .filter((r) => r.saleReturn > 0.01 || r.oe > 0.01 || r.ordinary > 0.01)
    .sort((a, b) => b.saleReturn - a.saleReturn);
}

function mergeSaleReturnIntoBrandRows(byBrand, byBrandSaleReturn) {
  const srMap = Object.fromEntries(byBrandSaleReturn.map((r) => [r.key, r.saleReturn]));
  for (const row of byBrand) {
    if (srMap[row.key] != null) row.saleReturn = srMap[row.key];
  }
}

function buildTotalsFromReport(report, byBrand, byGroup) {
  const footer = report?.footer || {};
  const totals = report?.totals || {};
  const brandSale = byBrand.reduce((s, r) => s + (r.sale || 0), 0);
  const brandPurchase = byBrand.reduce((s, r) => s + (r.purchase || 0), 0);
  const groupSale = byGroup.reduce((s, r) => s + (r.sale || 0), 0);
  const groupPurchase = byGroup.reduce((s, r) => s + (r.purchase || 0), 0);

  return {
    cashSale: totals.cashSale || 0,
    creditBill: totals.creditBill || 0,
    otherSale: totals.otherSale || 0,
    totalSale: footer.totalSale || 0,
    totalPurchase: footer.totalPurchase || 0,
    transferOut: footer.branchTransferOut || footer.transferOutCurr || 0,
    transferIn: footer.branchTransferIn || 0,
    lessSaleReturn: report?.collection?.lessSaleReturn || totals.lessSR || 0,
    brandLineSaleReturn: byBrand.reduce((s, r) => s + (r.saleReturn || 0), 0),
    brandLineSale: brandSale,
    brandLinePurchase: brandPurchase,
    groupLineSale: groupSale,
    groupLinePurchase: groupPurchase,
  };
}

/**
 * Generate full monthly import report + brand/group breakdown; save to Firestore.
 */
export async function generateAndSaveMonthlyImportPrecalc(
  { year, monthIndex, branchFilter = "Main", generatedBy = "" },
  onProgress
) {
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number(monthIndex)));

  onProgress?.("Computing monthly report…");
  const { fetchMonthlyAutosReport, clearImportMonthlyReportCache } = await import(
    "./monthlyAutosReportService"
  );
  const report = await fetchMonthlyAutosReport(y, mi, branchFilter, {
    importOnly: true,
    dateScope: "full",
    skipPrecalc: true,
  });

  onProgress?.("Aggregating brand & group lines…");
  const rows = await loadCurrentMonthRows(y, mi, branchFilter);
  const byBrand = aggregateRowsByDimension(rows, branchFilter, "brand");
  const byGroup = aggregateRowsByDimension(rows, branchFilter, "group");
  const byBrandSaleReturn = buildByBrandSaleReturn(report);
  mergeSaleReturnIntoBrandRows(byBrand, byBrandSaleReturn);
  if (branchFilter === "Branch 3") {
    for (const row of byBrand) {
      row.purchase = (Number(row.purchase) || 0) + (Number(row.transferIn) || 0);
      row.transferIn = 0;
    }
  }
  const totals = buildTotalsFromReport(report, byBrand, byGroup);

  const docId = monthlyImportPrecalcDocId(y, mi, branchFilter);
  const payload = {
    year: y,
    monthIndex: mi,
    branchFilter,
    monthLabel: REPORT_TITLE_MONTHS[mi],
    title: `MONTHLY IMPORT PRECALC - ${REPORT_TITLE_MONTHS[mi]} ${y} - ${branchFilter}`,
    generatedAt: new Date().toISOString(),
    generatedBy: String(generatedBy || ""),
    report,
    byBrand,
    byBrandSaleReturn,
    byGroup,
    totals,
    lineCount: rows.length,
    updatedAt: serverTimestamp(),
  };

  onProgress?.("Saving to Firestore…");
  await setDoc(doc(db, COL_MONTHLY_IMPORT_PRECALC, docId), payload, { merge: true });

  clearImportMonthlyReportCache();

  primePrecalcMemoryCache(docId, { id: docId, ...payload });

  return { docId, ...payload };
}

export function primePrecalcMemoryCache(docId, payload) {
  if (docId) precalcMemoryCache.set(docId, payload ?? null);
}

export function clearPrecalcMemoryCache() {
  precalcMemoryCache.clear();
  precalcInflight.clear();
}

const precalcMemoryCache = new Map();
const precalcInflight = new Map();

/** Load saved precalc snapshot (report + breakdowns). Dedupes concurrent reads. */
export async function loadMonthlyImportPrecalc({ year, monthIndex, branchFilter = "Main" }) {
  const docId = monthlyImportPrecalcDocId(year, monthIndex, branchFilter);
  if (precalcMemoryCache.has(docId)) return precalcMemoryCache.get(docId);
  if (precalcInflight.has(docId)) return precalcInflight.get(docId);

  const pending = (async () => {
    const snap = await getDoc(doc(db, COL_MONTHLY_IMPORT_PRECALC, docId));
    const result = snap.exists() ? { id: snap.id, ...snap.data() } : null;
    precalcMemoryCache.set(docId, result);
    precalcInflight.delete(docId);
    return result;
  })();
  precalcInflight.set(docId, pending);
  return pending;
}

export function mapPrecalcByGroupRows(precalc) {
  return (precalc?.byGroup || []).map((r) => ({
    key: String(r.key || r.label || "").trim(),
    sale: Number(r.sale) || 0,
  }));
}

/** Group-wise branch sale totals from precalc (fast) or live import aggregation. */
export async function fetchImportGroupSaleTotals(year, monthIndex, branchFilter = "Main") {
  const precalc = await loadMonthlyImportPrecalc({ year, monthIndex, branchFilter });
  if (precalc?.byGroup?.length) {
    return (precalc.byGroup || []).map((r) => ({
      key: String(r.key || r.label || "").trim(),
      sale: Number(r.sale) || 0,
    }));
  }
  const rows = await loadCurrentMonthRows(year, monthIndex, branchFilter);
  const byGroup = aggregateRowsByDimension(rows, branchFilter, "group");
  return byGroup.map((r) => ({
    key: String(r.key || r.label || "").trim(),
    sale: Number(r.sale) || 0,
  }));
}
